import { yellow, green } from '@ant-design/colors'
import {
  WarningTwoTone,
  CheckCircleTwoTone,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  ReloadOutlined,
  LoadingOutlined
} from '@ant-design/icons'
import { Popover, Divider, Popconfirm, Button, Table, List } from 'antd'
import { ColumnProps } from 'antd/es/table'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import DocumentationLink from '../Misc/DocumentationLink'
import LoadingWrapper from '../Misc/LoadingWrapper'
import { contextCompanyIdSelector } from '../../redux/context/company/selectors'
import { ReportDataType } from '../../redux/context/reports/types'
import { notificationAction } from '../../redux/middleware/actions'
import { currentUserSelector, currentUserIsAdminSelector } from '../../redux/session/currentUser/selectors'
import { useBackend } from '../../services/backend'
import { OneDrive } from '../../pages/settings/company/integrations/types'
import OneDriveModal from './OneDriveModal'
import { AppDispatch } from '../../redux/store'
import CtxScenarioName from '../../features/scenario/components/CtxScenarioName'

interface OneDriveProps {
  type: 'budget' | 'external' | 'actuals'
  dokumentationPath: string
}

enum columnNames {
  title = 'title',
  sheet = 'sheet',
  code = 'code',
  user = 'user',
  dataType = 'dataType',
  budgetingScenarioId = 'budgetingScenarioId',
  status = 'status',
  action = 'action'
}

const OneDrivePage: React.FC<OneDriveProps> = ({ type, dokumentationPath }: OneDriveProps) => {
  const { t } = useTranslation()
  const dispatch: AppDispatch = useDispatch()

  const companyId = useSelector(contextCompanyIdSelector)
  const currentUser = useSelector(currentUserSelector)
  const isAdmin = useSelector(currentUserIsAdminSelector)

  const [sheets, setSheets] = useState<OneDrive[]>([])
  const getSheetsRequest = useBackend(`/onedrive/sheets/{companyId}/${type}`, process.env.REACT_APP_INTEGRATION_URL)
  const getFolder = useBackend(`/onedrive/folder/{companyId}`, process.env.REACT_APP_INTEGRATION_URL)

  const deleteSheetRequest = useBackend(`/onedrive/sheets/{id}`, process.env.REACT_APP_INTEGRATION_URL)
  const refreshSheetRequest = useBackend(
    `/onedrive/sheets/{companyId}/{sheetId}/refresh`,
    process.env.REACT_APP_INTEGRATION_URL
  )

  const createFolderRequest = useBackend(`/onedrive/folder/{companyId}`, process.env.REACT_APP_INTEGRATION_URL)

  const [sheet, setSheet] = useState<OneDrive>()
  const [loadingId, setLoadingId] = useState(0)
  const [folder, setFolder] = useState(true)
  const [modalVisible, setModalVisible] = useState(false)
  const isOwner = (user: OneDrive['user']) => {
    return isAdmin || user?.id === currentUser?.id
  }

  const toggleModalVisible = () => {
    setSheet(undefined)
    setModalVisible(!modalVisible)
  }

  const toggleSheet = (sheetToUpdate: OneDrive) => {
    setSheet(sheetToUpdate)
    setModalVisible(true)
  }

  const refreshSheet = (sheetId: number) => {
    setLoadingId(sheetId)
    refreshSheetRequest
      .put({
        urlParams: { companyId, sheetId }
      })
      .then(() => {
        dispatch(
          notificationAction({
            type: 'success',
            message: 'UPDATE_INTEGRATION_SUCCESS'
          })
        )
        setLoadingId(0)
      })
      .catch(() => {
        dispatch(
          notificationAction({
            type: 'error',
            message: 'UPDATE_INTEGRATION_ERROR',
            description: 'VALUE_NOT_VALID'
          })
        )
        setLoadingId(0)
      })
  }

  const getSheetsHandler = () => {
    getSheetsRequest
      .get({
        urlParams: { companyId }
      })
      .then(setSheets)
      .catch((err: any) => {
        console.log(err)
      })
    getFolder
      .get({
        urlParams: { companyId }
      })
      .then((response: any) => setFolder(response.found))
      .catch((err: any) => {
        console.log(err)
      })
  }

  useEffect(getSheetsHandler, [companyId])

  const columns: ColumnProps<OneDrive>[] = []

  for (const [name] of Object.entries(columnNames)) {
    if (name === 'title') {
      columns.push({
        title: t('global:file'),
        key: 'name',
        render: ({ url }: OneDrive) => (
          <a href={url} target="_blank" rel="noopener noreferrer">
            {t('global:open_file')}
          </a>
        )
      })
    }
    if (name === 'sheet') {
      columns.push({
        title: t('integrationsPage:sheet'),
        key: 'sheet',
        dataIndex: 'sheet'
      })
    }
    if (name === 'code' && type === 'external') {
      columns.push({
        title: t('integrationsPage:code'),
        key: 'code',
        dataIndex: 'code'
      })
    }
    if (name === 'dataType' && type === 'external') {
      columns.push({
        title: t('global:type'),
        key: 'dataType',
        dataIndex: 'dataType',
        render: (dataType: ReportDataType) => {
          if (dataType === ReportDataType.budget) {
            return `${t(`global:${ReportDataType.budget}`)} / ${t(`global:${ReportDataType.forecast}`)}`
          }
          return t(`global:${dataType}`)
        }
      })
    }
    if (name === 'budgetingScenarioId' && (type === 'external' || type === 'budget')) {
      columns.push({
        title: t('global:scenario'),
        key: 'budgetingScenarioId',
        dataIndex: 'budgetingScenarioId',
        render: (id: number) => <CtxScenarioName scenarioId={id} />
      })
    }
    if (name === 'status') {
      columns.push({
        title: t('status:status'),
        key: 'status',
        render: ({ errors }: OneDrive) => {
          return errors ? (
            <Popover
              content={
                <List
                  itemLayout="horizontal"
                  dataSource={errors}
                  renderItem={error => (
                    <List.Item>
                      <List.Item.Meta description={t(`error:${error}`)} />
                    </List.Item>
                  )}
                />
              }
            >
              <WarningTwoTone twoToneColor={yellow.primary} />
            </Popover>
          ) : (
            <CheckCircleTwoTone twoToneColor={green.primary} />
          )
        }
      })
    }
    if (name === 'action') {
      columns.push({
        title: t('global:actions'),
        key: 'action',
        align: 'right',
        width: 100,
        render: (s: OneDrive) =>
          // eslint-disable-next-line react/destructuring-assignment
          isOwner(s.user) && (
            <span>
              {refreshSheetRequest.loading && loadingId === s.id ? (
                <LoadingOutlined style={{ fontSize: 14 }} spin />
              ) : (
                <ReloadOutlined onClick={() => refreshSheet(s.id)} />
              )}

              <Divider type="vertical" />
              <EditOutlined onClick={() => toggleSheet(s)} />
              <Divider type="vertical" />
              <Popconfirm
                placement="bottomRight"
                title={t('global:delete-confirm')}
                onConfirm={() => {
                  deleteSheetRequest
                    .delete({ urlParams: { id: s.id } })
                    .then((response: OneDrive) => {
                      dispatch(
                        notificationAction({
                          type: 'success',
                          message: 'DELETE_INTEGRATION_SUCCESS'
                        })
                      )
                      setSheets(sheets.filter(deleted => deleted.id !== response.id))
                    })
                    .catch(() => {
                      dispatch(
                        notificationAction({
                          type: 'error',
                          message: 'DELETE_INTEGRATION_ERROR'
                        })
                      )
                    })
                }}
                okText={t('global:yes')}
                cancelText={t('global:no')}
              >
                <DeleteOutlined style={{ color: 'red' }} />
              </Popconfirm>
            </span>
          )
      })
    }
  }

  const createFolder = () => {
    createFolderRequest
      .put({
        urlParams: { companyId },
        body: {
          data: {
            email: currentUser.email
          }
        }
      })
      .then(() => {
        setFolder(true)
        dispatch(
          notificationAction({
            type: 'success',
            message: 'CREATE_FOLDER_SUCCESS'
          })
        )
      })
      .catch(() => {
        setFolder(false)
        dispatch(
          notificationAction({
            type: 'error',
            message: 'ERROR'
          })
        )
      })
  }

  return (
    <>
      <div style={{ marginBottom: 8 }}>
        <DocumentationLink route={{ path: dokumentationPath }} />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        {folder === false ? (
          <Button
            type="primary"
            loading={getFolder.loading || createFolderRequest.loading}
            onClick={() => createFolder()}
          >
            <PlusOutlined />
            {t('integrationsPage:createFile')}
          </Button>
        ) : (
          <Button type="primary" onClick={toggleModalVisible}>
            <PlusOutlined />
            {t('global:add-new')}
          </Button>
        )}
      </div>
      <OneDriveModal
        sheets={sheets}
        setSheets={setSheets}
        sheet={sheet}
        modalVisible={modalVisible}
        toggleModalVisible={toggleModalVisible}
        type={type}
      />
      <LoadingWrapper loading={getSheetsRequest.loading}>
        <Table size="small" rowKey="id" dataSource={sheets} columns={columns} />
      </LoadingWrapper>
    </>
  )
}

export default OneDrivePage
